import { isPlatform } from "@ionic/core";

import { Filesystem, Directory, Encoding } from "@capacitor/filesystem";
import { Preferences } from "@capacitor/preferences";

import {
  IUser,
  IGuidelineSummary,
  IAlgorithm,
  ISignificantChange,
  ISignificantChangeAck,
  IAppFeature,
  IFurtherResource,
  IGuideline,
  IRemoteDataRequest,
  IScreenProps,
  IClinicalNotice,
  IClinicalNoticeAck,
  IPGDAck,
  IGLAck,
  IPathway,
  IAppSettings,
  validateIAppFeatures,
  validateIFurtherResources,
  validateIAppSettings,
  IAppData,
  ITrustPin,
  IPathwayBypassNumber,
  IHelpCentreItem,
  ITrustInfo,
  IPathwayWhatGoesWhere,
  ICFRbyTrustItem,
  IAnnotation,
  INotification,
  ISubscriptionPurchase,
  IAppEntitlementsResponse,
  IAppNotice,
} from "../Interfaces";
import DataController from "../controllers/DataController";

import { format } from "date-fns";
import { Subscriptions } from "capacitor-subscriptions";
import { FirebaseAnalytics } from "@capacitor-community/firebase-analytics";
import mixpanel from "mixpanel-browser";

let useInnodataGuidelines = false;

export class RemoteDataController {
  async getAimerEC2URL() {
    let serverUrl = "https://www.aimernginx.co.uk";

    let serverUrlData = await Preferences.get({ key: "serverUrl" });

    if (serverUrlData.value != null) {
      serverUrl = serverUrlData.value;
    }

    return serverUrl;
  }

  async downloadAppSettings() {
    try {
      const response = await fetch("https://aace.s3.amazonaws.com/appSettings.json", {
        headers: {
          "Cache-Control": "no-cache",
        },
      });
      let appSettings = await response.json();

      if (appSettings != null) {
        let isValid = validateIAppSettings(appSettings);

        if (isValid) {
          await Filesystem.writeFile({
            path: `appSettings.json`,
            data: JSON.stringify(appSettings),
            directory: Directory.Data,
            encoding: Encoding.UTF8,
          });
        } else {
          console.log("invalid app settings");
        }
      }
    } catch (error) {
      console.log("error saving appSettings.json: " + error);
    }
  }

  async downloadGuidelineSummaryUpdates(testMode: boolean) {
    let anyChanges = false;

    try {
      let url = testMode
        ? "https://aace.s3.amazonaws.com/GLUpdates.TEST.json"
        : "https://aace.s3.amazonaws.com/GLUpdates.json";

      if (useInnodataGuidelines) {
        url = `https://jrcalcassistant.s3.amazonaws.com/Innodata/GLList.json`;
      }
      const response = await fetch(url, {
        headers: {
          "Cache-Control": "no-cache",
        },
      });
      let guidelineSummaryUpdates = await response.json();

      if (guidelineSummaryUpdates != null && guidelineSummaryUpdates.length > 0) {
        await Filesystem.writeFile({
          path: `GLUpdates.json`,
          data: JSON.stringify(guidelineSummaryUpdates),
          directory: Directory.Data,
          encoding: Encoding.UTF8,
        });
      }
    } catch (error) {
      console.log("error saving GLUpdates: " + error);
    }

    return anyChanges;
  }

  async downloadTrustGuidelineSummaryUpdates(trust: string | null, testMode: boolean) {
    let anyChanges = false;

    if (trust == null || trust === "") {
      return anyChanges;
    }

    try {
      let url = testMode
        ? `https://aace.s3.amazonaws.com/${trust}/CMGLUpdates.${trust}.TEST.json`
        : `https://aace.s3.amazonaws.com/${trust}/CMGLUpdates.${trust}.json`;

      if (useInnodataGuidelines) {
        url = `https://jrcalcassistant.s3.amazonaws.com/Innodata/${trust}/CMGuidelineList.json`;
      }

      const response = await fetch(url, {
        headers: {
          "Cache-Control": "no-cache",
        },
      });
      let trustGuidelineSummaryUpdates = await response.json();

      if (trustGuidelineSummaryUpdates != null && trustGuidelineSummaryUpdates.length > 0) {
        await Filesystem.writeFile({
          path: `${trust}-CMGLUpdates.json`,
          data: JSON.stringify(trustGuidelineSummaryUpdates),
          directory: Directory.Data,
          encoding: Encoding.UTF8,
        });
      }
    } catch (error) {
      console.log("error saving trustCMGLUpdates: " + error);
    }

    return anyChanges;
  }

  async downloadCFRGuidelineSummaryUpdates(trust: string | null, testMode: boolean) {
    let anyChanges = false;

    if (trust == null || trust === "") {
      return anyChanges;
    }

    try {
      let url = testMode
        ? `https://aace.s3.amazonaws.com/${trust}/CFRIndex.${trust}.TEST.json`
        : `https://aace.s3.amazonaws.com/${trust}/CFRIndex.${trust}.json`;

      const response = await fetch(url, {
        headers: {
          "Cache-Control": "no-cache",
        },
      });
      let cfrTrustGuidelineSummaryUpdates = await response.json();

      if (cfrTrustGuidelineSummaryUpdates != null && cfrTrustGuidelineSummaryUpdates.length > 0) {
        await Filesystem.writeFile({
          path: `${trust}-CFRIndex.json`,
          data: JSON.stringify(cfrTrustGuidelineSummaryUpdates),
          directory: Directory.Data,
          encoding: Encoding.UTF8,
        });
      }
    } catch (error) {
      console.log("error saving CFRIndex: " + error);
    }

    return anyChanges;
  }

  async downloadAnnotations(trust: string | null, testMode: boolean, app: IScreenProps["app"]) {
    let anyChanges = false;

    if (trust == null || trust === "") {
      return anyChanges;
    }

    try {
      let url = testMode
        ? `https://aace.s3.amazonaws.com/${trust}/notesLists.${trust}.TEST.json`
        : `https://aace.s3.amazonaws.com/${trust}/notesLists.${trust}.json`;
      if (app === "CFR") {
        url = testMode
          ? `https://aace.s3.amazonaws.com/${trust}/${trust}-CFR-NotesList.TEST.json`
          : `https://aace.s3.amazonaws.com/${trust}/${trust}-CFR-NotesList.json`;
      }

      const response = await fetch(url, {
        headers: {
          "Cache-Control": "no-cache",
        },
      });
      if (response.status === 404) {
        return anyChanges;
      }
      let annotations = await response.json();

      let path = `notesLists.${trust}.json`;
      if (testMode) {
        path = `notesLists.${trust}.TEST.json`;
      }

      if (annotations != null) {
        await Filesystem.writeFile({
          path: path,
          data: JSON.stringify(annotations),
          directory: Directory.Data,
          encoding: Encoding.UTF8,
        });
      }
    } catch (error) {
      console.log("error saving annotations: " + error);
    }

    return anyChanges;
  }

  async downloadAnnotationImages(trust: string, testMode: boolean) {
    let anyChanges = false;

    try {
      let annotations = await DataController.getAnnotations(trust, testMode);

      if (annotations != null) {
        for (let annotationIndex = 0; annotationIndex < annotations.length; annotationIndex++) {
          let annotation = annotations[annotationIndex];
          // see if pathway images need to be downloaded if haven't already decided

          let annotationImages = annotation.Images;
          let annotationImagesNeedDownloading = false;

          if (annotationImages != null) {
            for (let annotationImageIndex = 0; annotationImageIndex < annotationImages.length; annotationImageIndex++) {
              let annotationImage = annotationImages[annotationImageIndex];

              let annotationImageName = DataController.getAnnotationImageName(annotation, annotationImage.filename);
              let imageExists = await DataController.checkImageExists(annotationImageName, trust);

              if (!imageExists) {
                annotationImagesNeedDownloading = true;
                break;
              }
            }
          }

          if (annotationImagesNeedDownloading) {
            await this.downloadAnnotationImage(annotation, trust);
            anyChanges = true;
          }
        }
      }
    } catch (error) {
      console.log("Error downloading annotation images");
    }

    return anyChanges;
  }

  async downloadAnnotationImage(annotation: IAnnotation, trust: string) {
    try {
      let annotationImages = annotation.Images;

      if (annotationImages != null) {
        for (let annotationImageIndex = 0; annotationImageIndex < annotationImages.length; annotationImageIndex++) {
          let annotationImage = annotationImages[annotationImageIndex];

          let annotationImageName = DataController.getAnnotationImageName(annotation, annotationImage.filename);

          let url = `https://aace.s3.amazonaws.com/${annotationImageName}`;

          if (trust && trust !== "") {
            url = `https://aace.s3.amazonaws.com/${trust}/${annotationImageName}`;
          }

          const response = await fetch(url, {
            headers: {
              "Cache-Control": "no-cache",
            },
          });

          if (response.status === 200) {
            if (response.body !== null) {
              let _image: any = await response.clone().blob();
              let reader = new FileReader();

              reader.readAsDataURL(_image);
              reader.onloadend = async function () {
                // let imageData: any = JSON.stringify(reader.result);

                // if (isPlatform('mobile') && !isPlatform('mobileweb')) {
                let imageData = reader.result as string;
                // }

                // console.log(imageData);

                await Filesystem.writeFile({
                  path: `images/${annotationImageName}`,
                  data: imageData,
                  directory: Directory.Data,
                });
              };
            }
          } else {
            console.log(`error fetching image: ${url}`);
          }
        }
      }
    } catch (error) {
      console.log("error downloading annotation image: " + error);
    }

    return null;
  }

  async downloadPathwayData(trust: string | null, testMode: boolean) {
    let anyChanges = false;

    if (trust == null || trust === "") {
      return anyChanges;
    }

    try {
      let url = testMode
        ? `https://aace.s3.amazonaws.com/${trust}/${trust}.pathway.TEST.json`
        : `https://aace.s3.amazonaws.com/${trust}/${trust}.pathway.json`;

      const response = await fetch(url, {
        headers: {
          "Cache-Control": "no-cache",
        },
      });
      if (response.status === 404) {
        return anyChanges;
      }

      let pathwayData = await response.json();

      if (pathwayData != null) {
        let path = `${trust}.pathway.json`;
        if (testMode) {
          path = `${trust}.pathway.TEST.json`;
        }

        await Filesystem.writeFile({
          path: path,
          data: JSON.stringify(pathwayData),
          directory: Directory.Data,
          encoding: Encoding.UTF8,
        });
      }
    } catch (error) {
      console.log("error saving pathwayData: " + error);
    }

    return anyChanges;
  }

  async downloadPathwayImages(trust: string, testMode: boolean) {
    let anyChanges = false;

    try {
      let pathwayData = await DataController.getPathwayData(trust, testMode);

      if (pathwayData != null) {
        for (let pathwayIndex = 0; pathwayIndex < pathwayData.Pathways.length; pathwayIndex++) {
          let pathway = pathwayData.Pathways[pathwayIndex];
          // see if pathway images need to be downloaded if haven't already decided

          let pathwayImages = pathway.fields.PathwayImage;
          let pathwayImagesNeedDownloading = false;

          if (pathwayImages != null) {
            for (let pathwayImageIndex = 0; pathwayImageIndex < pathwayImages.length; pathwayImageIndex++) {
              let pathwayImage = pathwayImages[pathwayImageIndex];

              let pathwayImageName = DataController.getPathWayImageName(pathway, pathwayImage.filename);
              let imageExists = await DataController.checkImageExists(pathwayImageName, trust);

              if (!imageExists) {
                pathwayImagesNeedDownloading = true;
                break;
              }
            }
          }

          if (pathwayImagesNeedDownloading) {
            await this.downloadPathwayImage(pathway, trust);
            anyChanges = true;
          }
        }
        for (let whatGoesWhereIndex = 0; whatGoesWhereIndex < pathwayData.Whatgoeswhere.length; whatGoesWhereIndex++) {
          let whatGoesWhere = pathwayData.Whatgoeswhere[whatGoesWhereIndex];
          // see if whatGoesWhere images need to be downloaded if haven't already decided

          let pathwayImages = whatGoesWhere.fields.PathwayImage;
          let pathwayImagesNeedDownloading = false;

          if (pathwayImages != null) {
            for (let pathwayImageIndex = 0; pathwayImageIndex < pathwayImages.length; pathwayImageIndex++) {
              let pathwayImage = pathwayImages[pathwayImageIndex];

              let pathwayImageName = DataController.getPathwayWhatGoesWhereImageName(
                whatGoesWhere,
                pathwayImage.filename
              );
              let imageExists = await DataController.checkImageExists(pathwayImageName, trust);

              if (!imageExists) {
                pathwayImagesNeedDownloading = true;
                break;
              }
            }
          }

          if (pathwayImagesNeedDownloading) {
            await this.downloadPathwayWhatGoesWhereImage(whatGoesWhere, trust);
            anyChanges = true;
          }
        }
      }
    } catch (error) {
      console.log("Error downloading pathway images");
    }

    return anyChanges;
  }

  async downloadPathwayImage(pathway: IPathway, trust: string) {
    try {
      let pathwayImages = pathway.fields.PathwayImage;

      if (pathwayImages != null) {
        for (let pathwayImageIndex = 0; pathwayImageIndex < pathwayImages.length; pathwayImageIndex++) {
          let pathwayImage = pathwayImages[pathwayImageIndex];

          let pathwayImageName = DataController.getPathWayImageName(pathway, pathwayImage.filename);

          let url = `https://aace.s3.amazonaws.com/${pathwayImageName}`;

          if (trust && trust !== "") {
            url = `https://aace.s3.amazonaws.com/${trust}/${pathwayImageName}`;
          }

          const response = await fetch(url, {
            headers: {
              "Cache-Control": "no-cache",
            },
          });

          if (response.status === 200) {
            if (response.body !== null) {
              let _image: any = await response.clone().blob();
              let reader = new FileReader();

              reader.readAsDataURL(_image);
              reader.onloadend = async function () {
                // let imageData: any = JSON.stringify(reader.result);

                // if (isPlatform('mobile') && !isPlatform('mobileweb')) {
                let imageData = reader.result as string;
                // }

                // console.log(imageData);

                await Filesystem.writeFile({
                  path: `images/${pathwayImageName}`,
                  data: imageData,
                  directory: Directory.Data,
                });
              };
            }
          } else {
            console.log(`error fetching image: ${url}`);
          }
        }
      }
    } catch (error) {
      console.log("error downloading pathway image: " + error);
    }

    return null;
  }

  async downloadPathwayWhatGoesWhereImage(pathway: IPathwayWhatGoesWhere, trust: string) {
    try {
      let pathwayImages = pathway.fields.PathwayImage;

      if (pathwayImages != null) {
        for (let pathwayImageIndex = 0; pathwayImageIndex < pathwayImages.length; pathwayImageIndex++) {
          let pathwayImage = pathwayImages[pathwayImageIndex];

          let pathwayImageName = DataController.getPathwayWhatGoesWhereImageName(pathway, pathwayImage.filename);

          let url = `https://aace.s3.amazonaws.com/${pathwayImageName}`;

          if (trust && trust !== "") {
            url = `https://aace.s3.amazonaws.com/${trust}/${pathwayImageName}`;
          }

          const response = await fetch(url, {
            headers: {
              "Cache-Control": "no-cache",
            },
          });

          if (response.status === 200) {
            if (response.body !== null) {
              let _image: any = await response.clone().blob();
              let reader = new FileReader();

              reader.readAsDataURL(_image);
              reader.onloadend = async function () {
                // let imageData: any = JSON.stringify(reader.result);

                // if (isPlatform('mobile') && !isPlatform('mobileweb')) {
                let imageData = reader.result as string;
                // }

                // console.log(imageData);

                await Filesystem.writeFile({
                  path: `images/${pathwayImageName}`,
                  data: imageData,
                  directory: Directory.Data,
                });
              };
            }
          } else {
            console.log(`error fetching image: ${url}`);
          }
        }
      }
    } catch (error) {
      console.log("error downloading pathway image: " + error);
    }

    return null;
  }

  async downloadGuidelinesAndImages(trust: string, app: IScreenProps["app"]) {
    let anyChanges = false;

    let guidelineSummaries = await DataController.getGuidelineSummaries(trust, app);

    for (let guidelineSummaryIndex = 0; guidelineSummaryIndex < guidelineSummaries.length; guidelineSummaryIndex++) {
      let guidelineSummary = guidelineSummaries[guidelineSummaryIndex];
      let guidelineNeedsDownloading = false;
      let guidelineImagesNeedDownloading = false;

      // don't download removed guidelines or their images
      if (guidelineSummary.remove != null && guidelineSummary.remove === 1) {
        continue;
      }

      let guidelineSummaryMajor = guidelineSummary.publ_major_version_no;
      let guidelineSummaryMinor = guidelineSummary.publ_minor_version_no;

      let bundledGuidelineIsLatestVersion = false;
      let downloadedGuidelineIsLatestVersion = false;

      if (guidelineSummary.GLID === "EM1660") {
        let test = "";
      }

      // check if bundled guideline is the latest version
      let bundledGuidelineVersion = null;
      if (useInnodataGuidelines === false) {
        bundledGuidelineVersion = await DataController.getBundledGuidelineVersion(guidelineSummary, trust);
      }

      if (bundledGuidelineVersion != null) {
        if (
          guidelineSummaryMajor === bundledGuidelineVersion.publ_major_version_no &&
          guidelineSummaryMinor === bundledGuidelineVersion.publ_minor_version_no
        ) {
          bundledGuidelineIsLatestVersion = true;
        }
      }

      // if not then check if downloaded guideline is the latest version
      if (!bundledGuidelineIsLatestVersion) {
        let downloadedGuideline = await DataController.getDownloadedGuideline(guidelineSummary);
        if (downloadedGuideline != null) {
          if (
            guidelineSummaryMajor === downloadedGuideline.publ_major_version_no &&
            guidelineSummaryMinor === downloadedGuideline.publ_minor_version_no
          ) {
            downloadedGuidelineIsLatestVersion = true;
          }
        }
      }

      // if not then need to download latest guideline
      if (!(bundledGuidelineIsLatestVersion || downloadedGuidelineIsLatestVersion)) {
        guidelineNeedsDownloading = true;
        guidelineImagesNeedDownloading = true;
      }

      // see if guideline images need to be downloaded if haven't already decided
      if (!guidelineImagesNeedDownloading) {
        if (guidelineSummary.images != null) {
          for (
            let guidelineImageIndex = 0;
            guidelineImageIndex < guidelineSummary.images.length;
            guidelineImageIndex++
          ) {
            let guidelineImage = guidelineSummary.images[guidelineImageIndex];

            let imageExists = await DataController.checkImageExists(guidelineImage.imageName, guidelineSummary.trust);

            if (!imageExists) {
              guidelineImagesNeedDownloading = true;
              break;
            }
          }
        }
      }

      if (guidelineNeedsDownloading) {
        await this.downloadGuideline(guidelineSummary);
        anyChanges = true;
      }

      if (guidelineImagesNeedDownloading) {
        await this.downloadGuidelineImages(guidelineSummary);
        anyChanges = true;
      }
    }

    return anyChanges;
  }

  async downloadGuideline(guidelineSummary: IGuidelineSummary) {
    try {
      let url = `https://aace.s3.amazonaws.com/${guidelineSummary.GLID}.${guidelineSummary.publ_major_version_no}.${guidelineSummary.publ_minor_version_no}.json`;
      if (useInnodataGuidelines) {
        url = `https://jrcalcassistant.s3.amazonaws.com/Innodata/${guidelineSummary.GLID}.${guidelineSummary.publ_major_version_no}.${guidelineSummary.publ_minor_version_no}.json`;
      }

      if (guidelineSummary.trust) {
        url = `https://aace.s3.amazonaws.com/${guidelineSummary.trust}/${guidelineSummary.GLID}.${guidelineSummary.publ_major_version_no}.${guidelineSummary.publ_minor_version_no}.json`;

        if (useInnodataGuidelines) {
          url = `https://jrcalcassistant.s3.amazonaws.com/Innodata/${guidelineSummary.trust}/json/${guidelineSummary.GLID}.json`;
        }
      }

      const response = await fetch(url, {
        headers: {
          "Cache-Control": "no-cache",
        },
      });
      let guidelineJson = await response.json();

      if (guidelineJson != null) {
        let guideline = guidelineJson.guideline;

        if (guidelineJson.guideline == null) {
          guideline = guidelineJson.CMG;
        }

        await Filesystem.writeFile({
          path: `guidelines/${guidelineSummary.GLID}.json`,
          data: JSON.stringify(guideline),
          directory: Directory.Data,
          encoding: Encoding.UTF8,
        });

        return guideline;
      }
    } catch (error) {
      if (guidelineSummary != null) {
        console.log(`error downloading guideline ${guidelineSummary.GLID}: ${error}`);
      } else {
        console.log(`error downloading guideline: ${error}`);
      }
    }

    return null;
  }

  async downloadGuidelineImages(guidelineSummary: IGuidelineSummary) {
    try {
      if (guidelineSummary.images) {
        for (let guidelineImageIndex = 0; guidelineImageIndex < guidelineSummary.images.length; guidelineImageIndex++) {
          let guidelineImage = guidelineSummary.images[guidelineImageIndex];

          if (guidelineImage.imageName.includes("EAST0003.34I-35633_-20728_0.1677515760000.jpg")) {
            let test = "";
          }

          let url = `https://aace.s3.amazonaws.com/${guidelineImage.imageName}`;
          if (useInnodataGuidelines) {
            url = `https://jrcalcassistant.s3.amazonaws.com/Innodata/img/${guidelineImage.imageName}`;
          }

          if (guidelineSummary.trust) {
            url = `https://aace.s3.amazonaws.com/${guidelineSummary.trust}/${guidelineImage.imageName}`;

            if (useInnodataGuidelines) {
              url = `https://jrcalcassistant.s3.amazonaws.com/Innodata/${guidelineSummary.trust}/img/${guidelineImage.imageName}`;
            }
          }

          const response = await fetch(url, {
            headers: {
              "Cache-Control": "no-cache",
            },
          });

          if (response.status !== 200) {
            console.log(`error fetching image: ${url}`);
          }

          if (response.body !== null && response.status !== 404) {
            let _image: any = await response.clone().blob();
            let reader = new FileReader();

            reader.readAsDataURL(_image);
            reader.onloadend = async function () {
              // let imageData: any = JSON.stringify(reader.result);

              // if (isPlatform('mobile') && !isPlatform('mobileweb')) {
              let imageData = reader.result as string;
              // }

              // console.log(imageData);

              await Filesystem.writeFile({
                path: `images/${guidelineImage.imageName}`,
                data: imageData,
                directory: Directory.Data,
              });
            };
          }
        }
      }
    } catch (error) {
      console.log("error downloading guideline image: " + error);
    }

    return null;
  }

  async downloadAlgorithms(testMode: boolean) {
    try {
      let url = testMode
        ? "https://aace.s3.amazonaws.com/AlgorithmList.TEST.json"
        : "https://aace.s3.amazonaws.com/AlgorithmList.json";

      const response = await fetch(url, {
        headers: {
          "Cache-Control": "no-cache",
        },
      });
      let downloadedAlgorithmList: IAlgorithm[] = await response.json();

      if (downloadedAlgorithmList != null && downloadedAlgorithmList.length > 0) {
        await Filesystem.writeFile({
          path: `AlgorithmList.json`,
          data: JSON.stringify(downloadedAlgorithmList),
          directory: Directory.Data,
          encoding: Encoding.UTF8,
        });

        // download images if updated - first algorithm contains updated date for all algorithms
        let localAlgorithms = await DataController.getAlgorithms();

        let firstDownloadedAlgorithm = downloadedAlgorithmList[0];
        let firstLocalAlgorithm = localAlgorithms[0];

        if (firstLocalAlgorithm.updated != undefined && firstDownloadedAlgorithm.updated != undefined) {
          var downloadAlgorithmsUpdatedDate = Date.parse(firstDownloadedAlgorithm.updated);
          var localAlgorithmsUpdatedDate = Date.parse(firstLocalAlgorithm.updated);
          if (downloadAlgorithmsUpdatedDate > localAlgorithmsUpdatedDate) {
            // download algorithm images
            await this.downloadAlgorithmImages(downloadedAlgorithmList);
          }
        }
      }
    } catch (error) {
      console.log("error downloading algorithms: " + error);
    }
  }

  async downloadAlgorithmImages(algorithmList: IAlgorithm[]) {
    try {
      for (let algorithmIndex = 0; algorithmIndex < algorithmList.length; algorithmIndex++) {
        let algorithm = algorithmList[algorithmIndex];

        if (algorithm.image != null) {
          if (algorithm.image === "G0860.Breech_Birth_Algorithm_-_Photos.1693839540000.jpg") {
            let test = "";
          }

          let url = `https://aace.s3.amazonaws.com/${algorithm.image}`;

          const response = await fetch(url, {
            headers: {
              "Cache-Control": "no-cache",
            },
          });

          if (response.status !== 200) {
            console.log(`error fetching image: ${url}`);
          }

          if (response.body !== null) {
            let _image: any = await response.clone().blob();
            let reader = new FileReader();

            reader.readAsDataURL(_image);
            reader.onloadend = async function () {
              let imageData = reader.result;

              await Filesystem.writeFile({
                path: `images/${algorithm.image}`,
                data: JSON.stringify(imageData),
                directory: Directory.Data,
                encoding: Encoding.UTF8,
              });
            };
          }
        }
      }
    } catch (error) {
      console.log("error downloading algorithm images: " + error);
    }
  }

  async downloadAppFeatures(testMode: boolean) {
    try {
      let url = testMode
        ? "https://aace.s3.amazonaws.com/appFeatures.TEST.json"
        : "https://aace.s3.amazonaws.com/appFeatures.json";

      const response = await fetch(url, {
        headers: {
          "Cache-Control": "no-cache",
        },
      });
      let downloadedAppFeatureList: IAppFeature[] = await response.json();

      if (downloadedAppFeatureList != null && downloadedAppFeatureList.length > 0) {
        let isValid = validateIAppFeatures(downloadedAppFeatureList);

        if (isValid) {
          await Filesystem.writeFile({
            path: `appFeatures.json`,
            data: JSON.stringify(downloadedAppFeatureList),
            directory: Directory.Data,
            encoding: Encoding.UTF8,
          });
        } else {
          console.log("invalid data appFeatures.json");
        }
      }
    } catch (error) {
      console.log("error downloading app features: " + error);
    }
  }

  async downloadAppNotices(testMode: boolean) {
    try {
      let url = testMode
        ? "https://aace.s3.amazonaws.com/appNotices.TEST.json"
        : "https://aace.s3.amazonaws.com/appNotices.json";

      const response = await fetch(url, {
        headers: {
          "Cache-Control": "no-cache",
        },
      });
      let downloadedAppNoticesList: IAppNotice[] = await response.json();

      if (downloadedAppNoticesList != null) {
        await Filesystem.writeFile({
          path: `appNotices.json`,
          data: JSON.stringify(downloadedAppNoticesList),
          directory: Directory.Data,
          encoding: Encoding.UTF8,
        });
      }
    } catch (error) {
      console.log("error downloading app notices: " + error);
    }
  }

  async downloadFurtherResources(testMode: boolean) {
    try {
      let url = testMode
        ? "https://aace.s3.amazonaws.com/furtherResources.TEST.json"
        : "https://aace.s3.amazonaws.com/furtherResources.json";

      const response = await fetch(url, {
        headers: {
          "Cache-Control": "no-cache",
        },
      });
      let downloadedFurtherResourceList: IFurtherResource[] = await response.json();

      if (downloadedFurtherResourceList != null && downloadedFurtherResourceList.length > 0) {
        let isValid = validateIFurtherResources(downloadedFurtherResourceList);

        if (isValid) {
          await Filesystem.writeFile({
            path: `furtherResources.json`,
            data: JSON.stringify(downloadedFurtherResourceList),
            directory: Directory.Data,
            encoding: Encoding.UTF8,
          });
        } else {
          console.log("invalid further resources");
        }

        await this.downloadFurtherResourceImages(downloadedFurtherResourceList);
      }
    } catch (error) {
      console.log("error downloading further resources: " + error);
    }
  }

  async downloadFurtherResourceImages(furtherResources: IFurtherResource[]) {
    try {
      let existingFurtherResourceImages = await Filesystem.readdir({
        path: "furtherResourceImages",
        directory: Directory.Data,
      });

      let existingFurtherResourceImagesFiles = existingFurtherResourceImages.files.map((file) => file.name);

      for (let furtherResourceIndex = 0; furtherResourceIndex < furtherResources.length; furtherResourceIndex++) {
        let furtherResource = furtherResources[furtherResourceIndex];

        if (furtherResource.iconUrl != null && furtherResource.iconUrl !== "" && furtherResource.iconFileName != null) {
          // check if image exists and if not download it
          if (existingFurtherResourceImagesFiles.includes(furtherResource.iconFileName) === false) {
            try {
              const response = await fetch(furtherResource.iconUrl, {
                headers: {
                  "Cache-Control": "no-cache",
                },
              });

              if (response.status !== 200) {
                console.log(`error fetching image: ${furtherResource.iconUrl}`);
              }

              if (response.body !== null) {
                let _image: any = await response.clone().blob();
                let reader = new FileReader();

                reader.readAsDataURL(_image);
                reader.onloadend = async function () {
                  let imageData = reader.result as string;

                  await Filesystem.writeFile({
                    path: `furtherResourceImages/` + furtherResource.iconFileName,
                    data: imageData,
                    directory: Directory.Data,
                  });
                };
              }
            } catch (error) {
              console.log("error downloading further resource image: " + error);
            }
          }
        }
      }
    } catch (error) {
      console.log("error downloading further resource images: " + error);
    }
  }

  async downloadSignificantChanges(user: IUser, app: IScreenProps["app"], testMode: boolean) {
    try {
      let params: any = {
        testMode: testMode,
        appType: app.toUpperCase(),
      };

      if (user.trust && user.trust !== "") {
        params.trust = user.trust;
      }

      let url = (await this.getAimerEC2URL()) + "/CN/sigChanges";

      let response = await fetch(url, {
        method: "POST",
        headers: {
          Accept: "application/json",
          "Content-Type": "application/json",
          "Cache-Control": "no-cache",
        },
        body: JSON.stringify(params),
        cache: "reload",
      });

      if (response.body !== null) {
        let json = await response.clone().json();

        if (json != null && json.success === true && json.body != null) {
          const entries = json.body;
          let array: ISignificantChange[] = [];

          for (let i = 0; i < entries.length; i++) {
            const content = entries[i];
            let title = content.sigchange_title;
            let GLID = content.sigchange_rootcontentlink;
            let id = content.GUID;
            let description = content.sigchange_description;
            let trust = null;
            if (content.trust) {
              trust = content.trust;
            }

            let updated;
            try {
              const timestamp = content.UpdateDate;
              const date = new Date(timestamp);

              updated = {
                date: format(date, "dd MMMM yyyy"),
                string: format(date, "do MMMM, yyyy HH:mm:ss"),
                dateObject: date.toISOString(),
              };
            } catch (error) {
              console.log(error);
              updated = null;
            }

            if (title != null && GLID != null && id != null && description != null && updated != null) {
              array.push({
                title: title,
                GLID: GLID,
                id: id,
                description: description,
                updated: updated,
                trust: trust,
              });
            } else {
              console.log("Significant change is missing fields");
            }
          }

          DataController.saveSignificantChanges(array);
        }
      }
    } catch (error) {
      console.log(error);
    }
  }

  async downloadSignificantChangeAcks(user: IUser) {
    try {
      if (user.uid != null) {
        let trust = user.trust;
        let url = (await this.getAimerEC2URL()) + "/CN/SigChangeAcks";

        let response = await fetch(url, {
          method: "POST",
          headers: {
            Accept: "application/json",
            "Content-Type": "application/json",
            "Cache-Control": "no-cache",
          },
          body: JSON.stringify({
            ContactID: user.uid,
            trust: trust,
            token: user.token,
          }),
          cache: "reload",
        });

        if (response.body !== null) {
          let json = await response.clone().json();

          if (json != null && json.body != null) {
            json = json.body;
          }

          // save CNAcks
          var onlineAcknowledgements: ISignificantChangeAck[] = json;
          var offlineAcknowledgements = await DataController.getSignificantChangeAcks();

          for (let ackIndex = 0; ackIndex < onlineAcknowledgements.length; ackIndex++) {
            let onlineAck = onlineAcknowledgements[ackIndex];
            onlineAck.cnid = onlineAck.cnid.toString(); // cnid from server is number but turning to string for easier comparison
            var matchingOfflineAckFound = false;

            for (let ackIndex = 0; ackIndex < offlineAcknowledgements.length; ackIndex++) {
              let offlineAck = offlineAcknowledgements[ackIndex];

              if (onlineAck.cnid === offlineAck.cnid && onlineAck.timestamp === offlineAck.timestamp) {
                matchingOfflineAckFound = true;
                break;
              }
            }

            // only save if it doesn't already exist
            if (!matchingOfflineAckFound) {
              let acknowledgedDate = new Date(onlineAck.timestamp);
              await DataController.saveSignificantChangeAck(onlineAck);
            }
          }
        }
      }
    } catch (error) {
      console.log(error);
    }
  }

  async acknowledgeSignificantChange(significantChangeAck: ISignificantChangeAck, user: IUser) {
    try {
      var now = new Date();
      var accessTime = now.toISOString();

      let params = {
        ContactID: significantChangeAck.contactID,
        trust: significantChangeAck.trust,
        CNID: significantChangeAck.cnid,
        title: significantChangeAck.title,
        timestamp: significantChangeAck.timestamp,
        token: user.token,
      };

      let request: IRemoteDataRequest = {
        type: "sigChangeAck",
        url: (await this.getAimerEC2URL()) + "/CN/sigChangeAck",
        parameters: params,
        httpMethod: "POST",
      };

      let response = await this.sendRequest(request, false);

      try {
        if (response.success) {
        } else {
          if (response.error !== undefined) {
            console.log("Error acknowledging significant change: ", response.error);
          }
        }
      } catch (error) {
        console.log(error);
      }
    } catch (e) {
      console.log(e);
    }
  }

  async downloadClinicalNotices(user: IUser, app: IScreenProps["app"], testMode: boolean) {
    try {
      let trust = user.trust;
      let url = (await this.getAimerEC2URL()) + "/CN/CNs";

      let status = testMode ? "Draft" : "Active";

      let response = await fetch(url, {
        method: "POST",
        headers: {
          Accept: "application/json",
          "Content-Type": "application/json",
          "Cache-Control": "no-cache",
        },
        body: JSON.stringify({
          // ContactID: user.uid,
          trust: user.trust,
          status: status,
          apptype: app.toUpperCase(),
          apiVersion: 2,
        }),
        cache: "reload",
      });

      if (response.status !== 404 && response.body !== null) {
        let json = await response.clone().json();

        if (json != null && json.body != null) {
          json = json.body;
        }

        let clinicalNotices: IClinicalNotice[] = [];

        for (let cnIndex = 0; cnIndex < json.length; cnIndex++) {
          let clinicalNotice = json[cnIndex];

          if (clinicalNotice != null) {
            clinicalNotices.push(clinicalNotice);
          }
        }

        let path = `${trust}-clinicalNotices.json`;
        if (testMode) {
          path = `${trust}-clinicalNotices.TEST.json`;
        }

        await Filesystem.writeFile({
          path: path,
          data: JSON.stringify(clinicalNotices),
          directory: Directory.Data,
          encoding: Encoding.UTF8,
        });
      }
    } catch (error) {
      console.log(error);
    }
  }

  async downloadClinicalNoticeAcks(user: IUser, app: IScreenProps["app"]) {
    try {
      if (user.uid != null) {
        var url = (await this.getAimerEC2URL()) + "/CN/CNAcks";

        let response = await fetch(url, {
          method: "POST",
          headers: {
            Accept: "application/json",
            "Content-Type": "application/json",
            "Cache-Control": "no-cache",
          },
          body: JSON.stringify({
            ContactID: user.uid,
            trust: user.trust,
            product: app.toUpperCase(),
          }),
        });

        if (response.status === 404) {
          return;
        }

        try {
          const responseJson = await response.json();

          if (responseJson.success) {
            // save CNAcks
            var onlineAcknowledgements: IClinicalNoticeAck[] = responseJson.body;
            var offlineAcknowledgements = await DataController.getClinicalNoticeAcks();

            for (let ackIndex = 0; ackIndex < onlineAcknowledgements.length; ackIndex++) {
              let onlineAck = onlineAcknowledgements[ackIndex];
              var matchingOfflineAckFound = false;

              for (let ackIndex = 0; ackIndex < offlineAcknowledgements.length; ackIndex++) {
                let offlineAck = offlineAcknowledgements[ackIndex];

                if (onlineAck.cnid === offlineAck.cnid && onlineAck.timestamp === offlineAck.timestamp) {
                  matchingOfflineAckFound = true;
                  break;
                }
              }

              // only save if it doesn't already exist
              if (!matchingOfflineAckFound) {
                let acknowledgedDate = new Date(onlineAck.timestamp);
                await DataController.saveClinicalNoticeAck(onlineAck);
              }
            }
          } else {
            if (responseJson.error !== undefined) {
              console.log("Error getting cnAcks: ", responseJson.error);
            }
          }
        } catch (error) {
          console.log(error);
        }
      }
    } catch (e) {
      console.log(e);
    }
  }

  async downloadPGDTrustInfo() {
    let anyChanges = false;

    try {
      let url = "https://ppp-temp-files.s3.amazonaws.com/data/pppTrustInfo.json";

      const response = await fetch(url, {
        headers: {
          "Cache-Control": "no-cache",
        },
      });
      let pgdTrustInfo = await response.json();

      if (pgdTrustInfo != null && pgdTrustInfo.length > 0) {
        await Filesystem.writeFile({
          path: `TrustInfo.json`,
          data: JSON.stringify(pgdTrustInfo),
          directory: Directory.Data,
          encoding: Encoding.UTF8,
        });
      }
    } catch (error) {
      console.log("error saving pgdTrustInfo: " + error);
    }

    return anyChanges;
  }

  async downloadExternalCSS() {
    let anyChanges = false;

    try {
      let url = "https://aace.s3.amazonaws.com/ExternalStyle.css";

      const response = await fetch(url, {
        headers: {
          "Cache-Control": "no-cache",
        },
      });
      let externalCSS = await response.text();

      if (externalCSS != null && externalCSS.length > 0) {
        await Filesystem.writeFile({
          path: `ExternalStyle.css`,
          data: externalCSS,
          directory: Directory.Data,
          encoding: Encoding.UTF8,
        });
      }
    } catch (error) {
      console.log("error saving externalCSS: " + error);
    }

    return anyChanges;
  }

  async downloadSectionColors(testMode: boolean) {
    let anyChanges = false;

    try {
      let url = testMode
        ? "https://aace.s3.amazonaws.com/sectionColors.TEST.json"
        : "https://aace.s3.amazonaws.com/sectionColors.json";

      const response = await fetch(url, {
        headers: {
          "Cache-Control": "no-cache",
        },
      });
      let sectionColors = await response.json();

      if (sectionColors != null && sectionColors.length > 0) {
        await Filesystem.writeFile({
          path: `sectionColors.json`,
          data: JSON.stringify(sectionColors),
          directory: Directory.Data,
          encoding: Encoding.UTF8,
        });
      }
    } catch (error) {
      console.log("error saving sectionColors: " + error);
    }

    return anyChanges;
  }

  async downloadTrustPins(trust: string, testMode: boolean) {
    let anyChanges = false;

    if (trust == null || trust === "") {
      return anyChanges;
    }

    try {
      let url = testMode
        ? `https://aace.s3.amazonaws.com/${trust}/trustPins.${trust}.TEST.json`
        : `https://aace.s3.amazonaws.com/${trust}/trustPins.${trust}.json`;

      const response = await fetch(url, {
        headers: {
          "Cache-Control": "no-cache",
        },
      });
      if (response.status === 404) {
        return anyChanges;
      }
      let trustPins = await response.json();

      if (trustPins != null) {
        let path = `${trust}-trustPins.json`;
        if (testMode) {
          path = `${trust}-trustPins.TEST.json`;
        }

        await Filesystem.writeFile({
          path: path,
          data: JSON.stringify(trustPins),
          directory: Directory.Data,
          encoding: Encoding.UTF8,
        });

        this.downloadTrustPinImages(trustPins);
      }
    } catch (error) {
      console.log("error saving trust pins: " + error);
    }

    return anyChanges;
  }

  async downloadTrustPinImages(trustPins: ITrustPin[]) {
    try {
      let existingTrustPinImages = await Filesystem.readdir({
        path: "furtherResourceImages",
        directory: Directory.Data,
      });
      let existingTrustPinImagesFiles = existingTrustPinImages.files.map((file) => file.name);

      for (let trustPinIndex = 0; trustPinIndex < trustPins.length; trustPinIndex++) {
        let trustPin = trustPins[trustPinIndex];

        if (trustPin.iconUrl != null && trustPin.iconUrl !== "" && trustPin.iconFileName != null) {
          // check if image exists and if not download it
          if (existingTrustPinImagesFiles.includes(trustPin.iconFileName) === false) {
            try {
              const response = await fetch(trustPin.iconUrl, {
                headers: {
                  "Cache-Control": "no-cache",
                },
              });

              if (response.status !== 200) {
                console.log(`error fetching image: ${trustPin.iconUrl}`);
              }

              if (response.body !== null) {
                let _image: any = await response.clone().blob();
                let reader = new FileReader();

                reader.readAsDataURL(_image);
                reader.onloadend = async function () {
                  let imageData = reader.result as string;

                  await Filesystem.writeFile({
                    path: `furtherResourceImages/` + trustPin.iconFileName,
                    data: imageData,
                    directory: Directory.Data,
                  });
                };
              }
            } catch (error) {
              console.log("error downloading trust pin image: " + error);
            }
          }
        }
        if (trustPin.iconDarkUrl != null && trustPin.iconDarkUrl !== "" && trustPin.iconDarkFileName != null) {
          // check if image exists and if not download it
          if (existingTrustPinImagesFiles.includes(trustPin.iconDarkFileName) === false) {
            try {
              const response = await fetch(trustPin.iconDarkUrl, {
                headers: {
                  "Cache-Control": "no-cache",
                },
              });

              if (response.status !== 200) {
                console.log(`error fetching image: ${trustPin.iconDarkUrl}`);
              }

              if (response.body !== null) {
                let _image: any = await response.clone().blob();
                let reader = new FileReader();

                reader.readAsDataURL(_image);
                reader.onloadend = async function () {
                  let imageData = reader.result as string;

                  await Filesystem.writeFile({
                    path: `furtherResourceImages/` + trustPin.iconDarkFileName,
                    data: imageData,
                    directory: Directory.Data,
                  });
                };
              }
            } catch (error) {
              console.log("error downloading trust pin image: " + error);
            }
          }
        }
      }
    } catch (error) {
      console.log("error downloading trust pin images: " + error);
    }
  }

  async downloadRegions(trust: string, testMode: boolean) {
    let anyChanges = false;

    if (trust == null || trust === "") {
      return anyChanges;
    }

    try {
      let url = testMode
        ? `https://aace.s3.amazonaws.com/${trust}/regions.${trust}.TEST.json`
        : `https://aace.s3.amazonaws.com/${trust}/regions.${trust}.json`;

      const response = await fetch(url, {
        headers: {
          "Cache-Control": "no-cache",
        },
      });
      if (response.status === 404) {
        return anyChanges;
      }
      let regions = await response.json();

      if (regions != null && regions.length > 0) {
        let path = `${trust}-regions.json`;
        if (testMode) {
          path = `${trust}-regions.TEST.json`;
        }

        await Filesystem.writeFile({
          path: path,
          data: JSON.stringify(regions),
          directory: Directory.Data,
          encoding: Encoding.UTF8,
        });
      }
    } catch (error) {
      console.log("error saving regions: " + error);
    }

    return anyChanges;
  }

  async downloadStaffGroups(trust: string, testMode: boolean) {
    let anyChanges = false;

    if (trust == null || trust === "") {
      return anyChanges;
    }

    try {
      let url = testMode
        ? `https://aace.s3.amazonaws.com/${trust}/staffGroups.${trust}.TEST.json`
        : `https://aace.s3.amazonaws.com/${trust}/staffGroups.${trust}.json`;

      const response = await fetch(url, {
        headers: {
          "Cache-Control": "no-cache",
        },
      });
      if (response.status === 404) {
        return anyChanges;
      }
      let staffGroups = await response.json();

      if (staffGroups != null && staffGroups.length > 0) {
        let path = `${trust}-staffGroups.json`;
        if (testMode) {
          path = `${trust}-staffGroups.TEST.json`;
        }

        await Filesystem.writeFile({
          path: path,
          data: JSON.stringify(staffGroups),
          directory: Directory.Data,
          encoding: Encoding.UTF8,
        });
      }
    } catch (error) {
      console.log("error saving staff groups: " + error);
    }

    return anyChanges;
  }

  async downloadRestrictedSections(trust: string, testMode: boolean) {
    let anyChanges = false;

    if (trust == null || trust === "") {
      return anyChanges;
    }

    try {
      let url = testMode
        ? `https://aace.s3.amazonaws.com/${trust}/restrictedSections.${trust}.TEST.json`
        : `https://aace.s3.amazonaws.com/${trust}/restrictedSections.${trust}.json`;

      const response = await fetch(url, {
        headers: {
          "Cache-Control": "no-cache",
        },
      });
      if (response.status === 404) {
        return anyChanges;
      }
      let restrictedSections = await response.json();

      if (restrictedSections != null) {
        let path = `${trust}-restrictedSections.json`;
        if (testMode) {
          path = `${trust}-restrictedSections.TEST.json`;
        }

        await Filesystem.writeFile({
          path: path,
          data: JSON.stringify(restrictedSections),
          directory: Directory.Data,
          encoding: Encoding.UTF8,
        });
      }
    } catch (error) {
      console.log("error saving regions: " + error);
    }

    return anyChanges;
  }

  async downloadHelpCentreItems(testMode: boolean) {
    try {
      let url = testMode
        ? "https://aace.s3.amazonaws.com/helpCentreItems.TEST.json"
        : "https://aace.s3.amazonaws.com/helpCentreItems.json";

      const response = await fetch(url, {
        headers: {
          "Cache-Control": "no-cache",
        },
      });
      let downloadedHelpCentreItems: IHelpCentreItem[] = await response.json();

      if (downloadedHelpCentreItems != null && downloadedHelpCentreItems.length > 0) {
        await Filesystem.writeFile({
          path: `helpCentreItems.json`,
          data: JSON.stringify(downloadedHelpCentreItems),
          directory: Directory.Data,
          encoding: Encoding.UTF8,
        });

        await this.downloadHelpCentreImages(downloadedHelpCentreItems);
      }
    } catch (error) {
      console.log("error downloading help centre items: " + error);
    }
  }

  async downloadHelpCentreImages(helpCentreItems: IHelpCentreItem[]) {
    try {
      let existingHelpCentreImages = await Filesystem.readdir({
        path: "helpImages",
        directory: Directory.Data,
      });
      let existingHelpCentreImageFiles = existingHelpCentreImages.files.map((file) => file.name);

      for (let helpCentreItemsIndex = 0; helpCentreItemsIndex < helpCentreItems.length; helpCentreItemsIndex++) {
        let helpCentreItem = helpCentreItems[helpCentreItemsIndex];

        if (helpCentreItem.imageUrl != null && helpCentreItem.imageUrl !== "" && helpCentreItem.imageFileName != null) {
          // check if image exists and if not download it
          if (existingHelpCentreImageFiles.includes(helpCentreItem.imageFileName) === false) {
            try {
              const response = await fetch(helpCentreItem.imageUrl, {
                headers: {
                  "Cache-Control": "no-cache",
                },
              });

              if (response.status !== 200) {
                console.log(`error fetching image: ${helpCentreItem.imageUrl}`);
              }

              if (response.body !== null) {
                let _image: any = await response.clone().blob();
                let reader = new FileReader();

                reader.readAsDataURL(_image);
                reader.onloadend = async function () {
                  let imageData = reader.result as string;

                  await Filesystem.writeFile({
                    path: `helpImages/` + helpCentreItem.imageFileName,
                    data: imageData,
                    directory: Directory.Data,
                  });
                };
              }
            } catch (error) {
              console.log("error downloading help centre image: " + error);
            }
          }
        }
      }
    } catch (error) {
      console.log("error downloading help centre images: " + error);
    }
  }

  async downloadTrustInfo(testMode: boolean) {
    try {
      let url = testMode
        ? "https://aace.s3.amazonaws.com/trustInfoTextItems.TEST.json"
        : "https://aace.s3.amazonaws.com/trustInfoTextItems.json";

      const response = await fetch(url, {
        headers: {
          "Cache-Control": "no-cache",
        },
      });
      let downloadedTrustInfo: ITrustInfo[] = await response.json();

      if (downloadedTrustInfo != null && downloadedTrustInfo.length > 0) {
        await Filesystem.writeFile({
          path: `trustInfoTextItems.json`,
          data: JSON.stringify(downloadedTrustInfo),
          directory: Directory.Data,
          encoding: Encoding.UTF8,
        });
      }
    } catch (error) {
      console.log("error downloading trust info text items: " + error);
    }
  }

  async downloadParaPassGuidelineQuizList() {
    let anyChanges = false;

    try {
      let url = "https://ppp-temp-files.s3.amazonaws.com/data/pppGuidelinesWithQuizzes.json";

      const response = await fetch(url, {
        headers: {
          "Cache-Control": "no-cache",
        },
      });
      let quizList = await response.json();

      if (quizList != null && quizList.length > 0) {
        await Filesystem.writeFile({
          path: `pppGuidelinesWithQuizzes.json`,
          data: JSON.stringify(quizList),
          directory: Directory.Data,
          encoding: Encoding.UTF8,
        });
      }
    } catch (error) {
      console.log("error saving pppGuidelinesWithQuizzes.json: " + error);
    }

    return anyChanges;
  }

  async downloadPGDAcks(user: IUser) {
    try {
      if (user.uid != null) {
        let url = (await this.getAimerEC2URL()) + "/aimerapi/class/PGD-server/getPGDs";

        let response = await fetch(url, {
          method: "POST",
          headers: {
            Accept: "*/*",
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            token: user.token,
            wyvernID: user.uid,
            trust: user.trust,
            skipWyvern: true,
          }),
        });

        try {
          const responseJson = await response.json();

          if (responseJson.success && responseJson.params != null) {
            // save PGDAcks
            var onlineAcknowledgements: IPGDAck[] = responseJson.params;

            await DataController.savePGDAcks(onlineAcknowledgements);
          } else {
            if (responseJson.error !== undefined) {
              console.log("Error getting pgd acks: ", responseJson.error);
            }
          }
        } catch (error) {
          console.log(error);
        }
      }
    } catch (e) {
      console.log(e);
    }
  }

  async downloadSinglePGDAck(GLID: string, user: IUser) {
    try {
      if (user.uid != null) {
        var url = (await this.getAimerEC2URL()) + "/aimerapi/class/PGD-server/getSinglePGD";

        let response = await fetch(url, {
          method: "POST",
          headers: {
            Accept: "*/*",
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            token: user.token,
            wyvernID: user.uid,
            trust: user.trust,
            GLID: GLID,
          }),
        });

        try {
          const responseJson = await response.json();

          if (responseJson.success && responseJson.params != null) {
            // save PGDAcks
            var onlineAck: IPGDAck = responseJson.params;
            let updatedPGDAcks = await DataController.savePGDAck(onlineAck);
            return updatedPGDAcks;
          } else {
            if (responseJson.error !== undefined) {
              console.log("Error getting single pgd ack: ", responseJson.error);
            }
          }
        } catch (error) {
          console.log(error);
        }
      }
    } catch (e) {
      console.log(e);
    }

    return null;
  }

  async acknowledgePGD(guidelineSummary: IGuidelineSummary, user: IUser) {
    if (user.uid != null) {
      var now = new Date();
      var accessTime = now.toISOString();

      var parameters = {
        trust: user.trust,
        token: user.token,
        wyvernID: user.uid,
        GLID: guidelineSummary.GLID,
        title: guidelineSummary.Title,
        timestamp: accessTime,
        versionNumber: `${guidelineSummary.publ_major_version_no}.${guidelineSummary.publ_minor_version_no}`,
      };

      let request: IRemoteDataRequest = {
        type: "pgdAck",
        url: (await this.getAimerEC2URL()) + "/aimerapi/class/PGDAck",
        parameters: parameters,
        httpMethod: "POST",
      };

      let response = await this.sendRequest(request, false);

      try {
        if (response.success) {
          return true;
        } else {
          if (response.error !== undefined) {
            console.log("Error acknowledging pgd: ", response.error);
          }
        }
      } catch (error) {
        console.log(error);
      }
    }

    return false;
  }

  async acknowledgeClinicalNotice(clinicalNoticeAck: IClinicalNoticeAck, app: IScreenProps["app"]) {
    var parameters = {
      ContactID: clinicalNoticeAck.contactID,
      CNID: clinicalNoticeAck.cnid,
      title: clinicalNoticeAck.title,
      timestamp: clinicalNoticeAck.timestamp,
      trust: clinicalNoticeAck.trust,
      product: app.toUpperCase(),
    };

    let request: IRemoteDataRequest = {
      type: "CNAck",
      url: (await this.getAimerEC2URL()) + "/CN/CNAck",
      parameters: parameters,
      httpMethod: "POST",
    };

    let response = await this.sendRequest(request, false);

    try {
      if (response.success) {
      } else {
        if (response.error !== undefined) {
          console.log("Error acknowledging clinical notice: ", response.error);
        }
      }
    } catch (error) {
      console.log(error);
    }
  }

  async acknowledgeGuideline(guidelineSummary: IGuidelineSummary, user: IUser, app: IScreenProps["app"]) {
    if (user.uid != null) {
      var now = new Date();
      var accessTime = now.toISOString();

      var parameters = {
        trust: user.trust,
        token: user.token,
        ContactID: user.uid,
        GLID: guidelineSummary.GLID,
        title: guidelineSummary.Title,
        timestamp: accessTime,
        product: app.toUpperCase(),
      };

      let request: IRemoteDataRequest = {
        type: "GLAck",
        url: (await this.getAimerEC2URL()) + "/CN/GLAck",
        parameters: parameters,
        httpMethod: "POST",
      };

      let response = await this.sendRequest(request, false);

      try {
        if (response.success) {
          return true;
        } else {
          if (response.error !== undefined) {
            console.log("Error acknowledging guideline: ", response.error);
          }
        }
      } catch (error) {
        console.log(error);
      }
    }

    return false;
  }

  async downloadGLAcks(user: IUser, app: IScreenProps["app"]) {
    try {
      if (user.uid != null) {
        let url = (await this.getAimerEC2URL()) + "/CN/GLAcks";

        let response = await fetch(url, {
          method: "POST",
          headers: {
            Accept: "*/*",
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            token: user.token,
            ContactID: user.uid,
            trust: user.trust,
            product: app.toUpperCase(),
          }),
        });

        try {
          const responseJson = await response.json();

          if (responseJson.success && responseJson.body != null) {
            // save GLAcks
            var onlineAcknowledgements: IGLAck[] = responseJson.body;

            for (let ackIndex = 0; ackIndex < onlineAcknowledgements.length; ackIndex++) {
              let onlineAck = onlineAcknowledgements[ackIndex];
              await DataController.saveGLAck(onlineAck);
            }
          } else {
            if (responseJson.error !== undefined) {
              console.log("Error getting gl acks: ", responseJson.error);
            }
          }
        } catch (error) {
          console.log(error);
        }
      }
    } catch (e) {
      console.log(e);
    }
  }

  async downloadCFRbyTrustItems(testMode: boolean) {
    try {
      let path = `CFRbyTrust.json`;
      if (testMode) {
        path = `CFRbyTrust.TEST.json`;
      }

      let url = testMode
        ? "https://aace.s3.amazonaws.com/CFR/CFRbyTrust.TEST.json"
        : "https://aace.s3.amazonaws.com/CFR/CFRbyTrust.json";

      const response = await fetch(url, {
        headers: {
          "Cache-Control": "no-cache",
        },
      });
      let downloadedCFRbyTrustItems: ICFRbyTrustItem[] = await response.json();

      if (downloadedCFRbyTrustItems != null && downloadedCFRbyTrustItems.length > 0) {
        await Filesystem.writeFile({
          path: path,
          data: JSON.stringify(downloadedCFRbyTrustItems),
          directory: Directory.Data,
          encoding: Encoding.UTF8,
        });
      }
    } catch (error) {
      console.log("error downloading CFRbyTrustItems: " + error);
    }
  }

  async downloadNotifications(user: IUser, app: IScreenProps["app"]) {
    try {
      let params: any = {
        appType: app.toUpperCase(),
        trust: user.trust,
        ContactID: user.uid,
        token: user.token,
        topics: user.pushNotificationTopics,
      };

      let url = (await this.getAimerEC2URL()) + "/CN/notifications";

      let response = await fetch(url, {
        method: "POST",
        headers: {
          Accept: "application/json",
          "Content-Type": "application/json",
          "Cache-Control": "no-cache",
        },
        body: JSON.stringify(params),
        cache: "reload",
      });

      if (response.body !== null) {
        let json = await response.clone().json();

        if (json != null && json.success === true && json.body != null) {
          const entries = json.body;
          let array: INotification[] = [];

          for (let i = 0; i < entries.length; i++) {
            const content: INotification = entries[i];
            if (content.id != null) {
              array.push(content);
            } else {
              console.log("Notification is missing fields");
            }
          }

          DataController.saveNotifications(array);
        }
      }
    } catch (error) {
      console.log(error);
    }
  }

  async downloadTrendingSearches(user: IUser, app: IScreenProps["app"], testMode: boolean) {
    try {
      let params: any = {
        appType: app.toUpperCase(),
        trust: user.trust,
        contactID: user.uid,
        token: user.token,
      };

      let url = (await this.getAimerEC2URL()) + "/TA-2/aimerapi/class/CN/Query/TrendingSearches";

      let response = await fetch(url, {
        method: "POST",
        headers: {
          Accept: "application/json",
          "Content-Type": "application/json",
        },
        body: JSON.stringify(params),
        cache: "reload",
      });

      if (response !== null) {
        let json = await response.clone().json();

        if (json != null && json.success === true && json.items != null) {
          const entries = json.items;

          let path = `${user.trust}-trendingSearches.json`;
          if (testMode) {
            path = `${user.trust}-trendingSearches.TEST.json`;
          }

          await Filesystem.writeFile({
            path: path,
            data: JSON.stringify(entries),
            directory: Directory.Data,
            encoding: Encoding.UTF8,
          });
        }
      }
    } catch (error) {
      console.log(error);
    }
  }

  async logUsage(
    guideline: IGuideline | null,
    algorithm: IAlgorithm | null,
    clinicalNotice: IClinicalNotice | null,
    pathway: { id: string; name: string; updated: string } | null,
    essential: any,
    appStart: boolean,
    appStartVersion: string,
    user: IUser | null,
    app: IScreenProps["app"]
  ) {
    try {
      if (user != null && user.uid != null && user.trust != null) {
        var now = new Date();
        var accessTime = now.toISOString();

        let GLID = "";
        let title = "";
        let versionNumber = "unknown";
        let GLType = "";

        if (guideline != null) {
          GLID = guideline.GLID;
          title = guideline.title;
          versionNumber = guideline.publ_major_version_no + "." + guideline.publ_minor_version_no;
          GLType = guideline.publ_guideline_type;
        } else if (algorithm != null) {
          GLID = "ALG-" + algorithm.id;
          title = algorithm.name;
        } else if (pathway != null) {
          GLID = "PW:" + pathway.id;
          title = pathway.name;
          versionNumber = pathway.updated;
        } else if (clinicalNotice != null) {
          GLID = "CNID:-" + clinicalNotice.ID;
          title = clinicalNotice.Title;
          if (clinicalNotice.updated != null) {
            versionNumber = clinicalNotice.updated;
          }
        } else if (essential != null) {
          let chapterNumber: string = essential.chapter;

          if (essential.chapter.includes("Chapter")) {
            chapterNumber = essential.chapter.substr(8, essential.chapter.indexOf(":") - 8);
          }

          GLID = "ESSENTIAL-" + chapterNumber + "-" + essential.sectionNumber;
          title = essential.chapter + " - Section " + essential.sectionNumber;
        } else if (appStart) {
          GLID = "AppStart";
          title = "AppStart";
          versionNumber = appStartVersion;
        }

        var parameters = {
          wyvernID: user.uid,
          GLID: GLID,
          title: title,
          timestamp: accessTime,
          trust: user.trust,
          versionNumber: versionNumber,
          GLType: GLType,
          product: app.toUpperCase(),
        };

        let request: IRemoteDataRequest = {
          type: "logGL",
          url: (await this.getAimerEC2URL()) + "/aimerapi/class/logGL",
          parameters: parameters,
          httpMethod: "POST",
        };

        await this.sendRequest(request, false);
      }
    } catch (error) {
      console.log("Error logging usage: " + error);
    }
  }

  logEvent(event: string, params: object) {
    try {
      FirebaseAnalytics.logEvent({ name: event, params: params });
      mixpanel.track(event, params);
    } catch (error) {
      console.log(error);
    }
  }

  setUserProperty(name: string, value: string) {
    try {
      FirebaseAnalytics.setUserProperty({ name: name, value: value });

      if (mixpanel != null && mixpanel.people != null) {
        let mixPanelParams: any = {};
        mixPanelParams[name] = value;
        mixpanel.people.set(mixPanelParams);
      }
    } catch (error) {
      console.log(error);
    }
  }

  async logSubscriptionRestorationInfo(message: string) {
    try {
      if (message != null && message !== "") {
        let response = await fetch("https://www.aimernginx.co.uk/logit.php?argument1=SUBS&argument2=" + message);
      }
    } catch (error) {
      console.log("Error sending subscription restoration info: " + error);
    }
  }

  async sendUsageLog(user: IUser, app: IScreenProps["app"]) {
    return new Promise<boolean>(async (resolve, reject) => {
      try {
        if (user.uid == null) {
          reject("Temporary user");
          return;
        }

        let url = (await this.getAimerEC2URL()) + "/classapi/SF/emailUsageLog";

        if (user.token == null || user.token === "") {
          console.log("Error getting items: No token provided");
          reject("No token provided");
        }

        var response = await fetch(url, {
          method: "POST",
          headers: {
            Accept: "application/json",
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            ContactID: user.uid + "",
            token: user.token,
            product: app.toUpperCase(),
            email: user.email,
          }),
        });

        if (response.status === 401) {
          reject("unauthorised");
          return;
        }

        let responseJson = await response.json();
        if (response.status === 200 && responseJson.success === true) {
          resolve(true);
        } else {
          reject(null);
        }
      } catch (error) {
        console.log(error);
        reject(null);
      }
    });
  }

  mapGoogleSub(subscription: any): ISubscriptionPurchase {
    // console.log('mapGoogleSub', subscription);
    const endDate = new Date(subscription.expiryDate).toISOString();

    const dateString = subscription.originalStartDate;
    const dateSplit = dateString.split(" ");
    const date = dateSplit[0].split("-");
    const time = dateSplit[1].split(":");

    const startDate = new Date(date[2], date[1] - 1, date[0], time[0], time[1]).toISOString();

    const mappedSubscription: ISubscriptionPurchase = {
      productId: subscription.productIdentifier,
      transactionId: subscription.transactionId,
      startDate,
      endDate,
    };

    return mappedSubscription;
  }

  async restoreSubscription(): Promise<IAppEntitlementsResponse> {
    return new Promise(async (resolve, reject) => {
      try {
        let response: IAppEntitlementsResponse = { success: true, subs: [] };

        if (isPlatform("android")) {
          response = await this.getEntitlementsFromGoogle();
        } else if (isPlatform("ios")) {
          response = await this.getEntitlementsFromApple();
        }

        resolve(response);
      } catch (error: any) {
        console.log(error);
        reject(error);
      }
    });
  }

  async getEntitlementsFromApple(): Promise<IAppEntitlementsResponse> {
    return new Promise(async (resolve, reject) => {
      try {
        let subs: ISubscriptionPurchase[] = [];
        let success = true;

        // Calling getCurrentEntitlements() will return an array of subscription transactions which are still ACTIVE.
        const response = await Subscriptions.getCurrentEntitlements();
        // console.log('entitlementsResponse (Apple): ', response.data);

        /**
         * response codes and matching messages:
         * -1: Incompatible with web
         * 0: Successfully found all entitlements across all product types
         * 1: No entitlements were found
         * 2: Unknown problem trying to retrieve entitlements
         */
        if (response.responseCode === 2) {
          success = false;
        } else if (response.responseCode === 0) {
          const data = response.data as any;

          if (data) {
            const keys = Object.keys(data);

            keys.forEach((key) => {
              subs.push({
                endDate: data[key].expiryDate,
                productId: data[key].productIdentifier,
                transactionId: data[key].transactionId,
                startDate: data[key].originalStartDate,
              });
            });
          }
        }

        resolve({ success, subs });
      } catch (error: any) {
        console.log(error);
        reject({ success: false, subs: [] });
      }
    });
  }

  async getEntitlementsFromGoogle(): Promise<IAppEntitlementsResponse> {
    return new Promise(async (resolve, reject) => {
      try {
        let subs: ISubscriptionPurchase[] = [];
        let success = true;

        // Calling getCurrentEntitlements() will return an array of subscription transactions which are still ACTIVE.
        const response = await Subscriptions.getCurrentEntitlements();
        // console.log('entitlementsResponse (Google): ', response);
        // console.log('entitlementsResponseData (Google): ', response.data);

        /**
         * response codes and matching messages:
         * -1: Incompatible with web
         * 0: Successfully found all entitlements across all product types
         * 1: No entitlements were found
         * 2: Unknown problem trying to retrieve entitlements
         */
        if (response.responseCode === 2) {
          success = false;
        } else if (response.responseCode === 0) {
          const data = response.data;

          if (data) {
            const array: [] = JSON.parse(data.toString());

            array.forEach((s) => {
              subs.push(this.mapGoogleSub(s));
            });
          }
        }

        resolve({ success, subs });
      } catch (error: any) {
        console.log(error);
        reject({ success: false, subs: [] });
      }
    });
  }

  async sendPurchaseInfo(
    subscriptionID: number,
    user: IUser,
    transactionDate: string,
    transactionIdentifier: string,
    productIdentifier: string,
    endDate: string
  ): Promise<boolean> {
    if (user.uid == null) {
      return false;
    }

    let url = (await this.getAimerEC2URL()) + "/classapi/SF/pppapi/sendPurchaseInfo";

    var deviceType = "iOS";
    if (isPlatform("android")) {
      deviceType = "Android";
    }

    let response = await fetch(url, {
      method: "POST",
      headers: {
        Accept: "application/json",
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        subscriptionID: subscriptionID,
        contactID: user.uid,
        transactionDate: transactionDate,
        transactionIdentifier: transactionIdentifier,
        productIdentifier: productIdentifier,
        deviceType: deviceType,
        token: user.token,
        expiryDate: endDate,
      }),
    });

    try {
      const responseJson = await response.json();

      if (responseJson.success) {
        return true;
      }

      if (responseJson.error !== undefined) {
        console.log("Error sending request: ", responseJson.error);
        return false;
      }
    } catch (error) {
      console.log(error);
      return false;
    }

    return false;
  }

  async uploadAppData(appData: IAppData, subscriberID: string, user: IUser) {
    if (user.uid == null) {
      return false;
    }

    var parameters = {
      ContactID: user.uid,
      token: user.token,
      SubscriberID: subscriberID,
      AppData: JSON.stringify(appData),
    };

    let request: IRemoteDataRequest = {
      type: "AppData",
      url: (await this.getAimerEC2URL()) + "/classapi/SF/updateSubAppData",
      parameters: parameters,
      httpMethod: "POST",
    };

    let response = await this.sendRequest(request, false);

    try {
      if (response.success) {
      } else {
        if (response.error !== undefined) {
          console.log("Error uploading app data: ", response.error);
        }
      }
    } catch (error) {
      console.log(error);
    }
  }

  async updateStaffGroups(staffGroups: string, subscriberID: string, user: IUser) {
    if (user.uid == null) {
      return false;
    }

    var parameters = {
      ContactID: user.uid,
      token: user.token,
      SubscriberID: subscriberID,
      StaffGroups: staffGroups,
    };

    let request: IRemoteDataRequest = {
      type: "StaffGroups",
      url: (await this.getAimerEC2URL()) + "/classapi/SF/updateStaffGroups",
      parameters: parameters,
      httpMethod: "POST",
    };

    let response = await this.sendRequest(request, false);

    try {
      if (response.success) {
      } else {
        if (response.error !== undefined) {
          console.log("Error uploading app data: ", response.error);
        }
      }
    } catch (error) {
      console.log(error);
    }
  }

  async updateRegions(regions: string, subscriberID: string, user: IUser) {
    if (user.uid == null) {
      return false;
    }

    var parameters = {
      ContactID: user.uid,
      token: user.token,
      SubscriberID: subscriberID,
      Region: regions,
    };

    let request: IRemoteDataRequest = {
      type: "Region",
      url: (await this.getAimerEC2URL()) + "/classapi/SF/updateRegion",
      parameters: parameters,
      httpMethod: "POST",
    };

    let response = await this.sendRequest(request, false);

    try {
      if (response.success) {
      } else {
        if (response.error !== undefined) {
          console.log("Error uploading app data: ", response.error);
        }
      }
    } catch (error) {
      console.log(error);
    }
  }

  async sendPathwayFeedback(
    pathwayID: string,
    pathwayName: string,
    locationID: string,
    locationName: string,
    locationPhoneNumber: string,
    feedback: string,
    user: IUser
  ) {
    if (user.uid == null) {
      return false;
    }

    var parameters = {
      ContactID: user.uid,
      token: user.token,
      Trust: user.trust,
      PathwayID: pathwayID,
      PathwayName: pathwayName,
      LocationID: locationID,
      LocationName: locationName,
      LocationPhoneNumber: locationPhoneNumber,
      Feedback: feedback,
    };

    let request: IRemoteDataRequest = {
      type: "PathwayFeedback",
      url: (await this.getAimerEC2URL()) + "/classapi/SF/sendPathwayFeedback",
      parameters: parameters,
      httpMethod: "POST",
    };

    let response = await this.sendRequest(request, false);

    try {
      if (response.success) {
      } else {
        if (response.error !== undefined) {
          console.log("Error sending pathway feedback: ", response.error);
        }
      }
    } catch (error) {
      console.log(error);
    }
  }

  /** Use this function for sending any requests which need to be saved on the server (as they will get saved for resending if they fail). */
  async sendRequest(request: IRemoteDataRequest, resendingFromQueue: boolean) {
    try {
      if (resendingFromQueue === false) {
        await this.addToRemoteQueue(request);
      }

      var params = JSON.stringify(request.parameters);

      let response = await fetch(request.url, {
        method: request.httpMethod,
        headers: {
          Accept: "application/json",
          "Content-Type": "application/json",
        },
        body: params,
      });

      try {
        const responseJson = await response.json();

        if (responseJson.success) {
          await this.removeFromRemoteQueue(request);
        }

        if (responseJson.error !== undefined) {
          console.log("Error sending request: ", responseJson.error);
        }

        return responseJson;
      } catch (error) {
        console.log(error);
      }
    } catch (e) {
      console.log(e);
    }
  }

  async getRemoteQueue(): Promise<IRemoteDataRequest[]> {
    let remoteQueue = [];

    try {
      let remoteQueueJSONString = await Preferences.get({ key: "remoteQueue" });

      if (remoteQueueJSONString.value != null) {
        remoteQueue = JSON.parse(remoteQueueJSONString.value);
      }
    } catch (error) {
      console.log(error);
    }

    return remoteQueue;
  }

  async addToRemoteQueue(queueItem: IRemoteDataRequest) {
    try {
      let remoteQueue = await this.getRemoteQueue();
      remoteQueue.push(queueItem);

      await Preferences.set({ key: "remoteQueue", value: JSON.stringify(remoteQueue) });
    } catch (error) {
      console.log(error);
    }
  }

  async removeFromRemoteQueue(queueItem: IRemoteDataRequest) {
    try {
      let remoteQueue = await this.getRemoteQueue();

      for (let i = 0; i < remoteQueue.length; i++) {
        var aQueueItem = remoteQueue[i];

        if (JSON.stringify(queueItem) === JSON.stringify(aQueueItem)) {
          remoteQueue.splice(i, 1);
          break;
        }
      }

      await Preferences.set({ key: "remoteQueue", value: JSON.stringify(remoteQueue) });
    } catch (error) {
      console.log(error);
    }
  }

  async sendRemoteQueue() {
    try {
      let remoteQueue = await this.getRemoteQueue();

      for (let i = 0; i < remoteQueue.length; i++) {
        var aQueueItem = remoteQueue[i];

        await this.sendRequest(aQueueItem, true);
      }
    } catch (error) {
      console.log(error);
    }
  }
}

export default new RemoteDataController();
